import { showCustomMessage } from "@/methods/utils";
import { useAppSettingsStore } from "@/stores/core/appSettingsStore";
import { useApiServicesStore } from "@/stores/core/apiServicesStore";
import { useSessionStore } from "@/stores/core/sessionStore";
import { IdNamePair, StudentStore } from "@/types/global";
import type { EnrolledCourseDisplay } from "@/types/userManagement";
import { defineStore, storeToRefs } from "pinia";

function getInitialStudentStore(): StudentStore {
  const initialStudentStore: StudentStore = {
    ActiveCourseIndex: 0,
    ActiveClassIndex: 0,
    AllSubjectsCards: {},
    AllRewardCoins: null,
    AllStudentContent: {},
    AllSubjectTrends: {},
    AllOverviewCards: {},
    SelectedSubject: null,
    SelectedSubjectChapterIds: [],
    EnrolledCourses: [],
    DataLoading: false,
  };
  return JSON.parse(JSON.stringify(initialStudentStore));
}

export const useStudentStore = defineStore("studentStore", {
  state: (): StudentStore => getInitialStudentStore(),
  getters: {
    SelectedChapterNames(): Array<string> | null {
      if (
        !this.ActiveClass ||
        !this.SelectedSubject ||
        this.SelectedSubjectChapterIds.length === 0
      ) {
        return null;
      }
      const activeKey = `${this.ActiveClass?.Id}_${this.SelectedSubject.Id}`;
      if (!(this.AllStudentContent[activeKey] && this.AllStudentContent[activeKey].cards)) {
        return [];
      }
      return this.AllStudentContent[activeKey].cards
        .filter((c) => this.SelectedSubjectChapterIds.includes(c.cardInfo.Id))
        .map((c) => c.cardInfo.Name);
    },
    ActiveCourse(): EnrolledCourseDisplay | null {
      return this.EnrolledCourses[this.ActiveCourseIndex] ?? null;
    },
    ActiveClass(): IdNamePair | null {
      return this.ActiveCourse ? this.ActiveCourse.Classes[this.ActiveClassIndex] : null;
    },
    AllSubjects(): Array<IdNamePair> | null {
      if (!this.ActiveCourse) {
        return null;
      }
      const distinctSubjects = new Map<number, IdNamePair>();
      this.ActiveCourse.Subjects.filter((subject) => subject.IsActive)
        .sort((firstSubject, secondSubject) => firstSubject.DisplayRank - secondSubject.DisplayRank)
        .forEach((subject) => {
          if (!distinctSubjects.has(subject.SubjectId)) {
            distinctSubjects.set(subject.SubjectId, {
              Id: subject.SubjectId,
              Name: subject.SubjectName,
            });
          }
        });
      return distinctSubjects.size > 0 ? Array.from(distinctSubjects.values()) : null;
    },
    // Same list as AllSubjects but keyed on the Course↔Subject mapping id — feed this to any
    // endpoint whose filter is a courseSubjectId rather than the master SubjectId.
    AllCourseSubjects(): Array<IdNamePair> | null {
      if (!this.ActiveCourse) {
        return null;
      }
      const courseSubjects = this.ActiveCourse.Subjects.filter((subject) => subject.IsActive)
        .sort((firstSubject, secondSubject) => firstSubject.DisplayRank - secondSubject.DisplayRank)
        .map((subject) => ({ Id: subject.CourseSubjectId, Name: subject.SubjectName }));
      return courseSubjects.length > 0 ? courseSubjects : null;
    },
    AllClasses(): Array<IdNamePair> | null {
      return this.ActiveCourse && this.ActiveCourse.Classes.length > 0
        ? this.ActiveCourse.Classes
        : null;
    },
    CourseSubjectIdBySubjectId(): Record<number, number> {
      const subjectIdToCourseSubjectId: Record<number, number> = {};
      if (!this.ActiveCourse) {
        return subjectIdToCourseSubjectId;
      }
      for (const subject of this.ActiveCourse.Subjects) {
        if (subject.IsActive && subjectIdToCourseSubjectId[subject.SubjectId] === undefined) {
          subjectIdToCourseSubjectId[subject.SubjectId] = subject.CourseSubjectId;
        }
      }
      return subjectIdToCourseSubjectId;
    },
    SubjectNameByCourseSubjectId(): Record<number, string> {
      const courseSubjectIdToName: Record<number, string> = {};
      if (!this.ActiveCourse) {
        return courseSubjectIdToName;
      }
      for (const subject of this.ActiveCourse.Subjects) {
        courseSubjectIdToName[subject.CourseSubjectId] = subject.SubjectName;
      }
      return courseSubjectIdToName;
    },
  },
  actions: {
    async getEnrolledCourses(forceRefresh = false) {
      if (forceRefresh) {
        this.$reset();
      }
      if (this.EnrolledCourses.length > 0) {
        return;
      }
      const apiServicesStore = useApiServicesStore();
      const { studentCourseApi } = storeToRefs(apiServicesStore);
      this.DataLoading = true;
      const response = await studentCourseApi.value.getEnrolledCourses();
      if (response.isSuccess) {
        // Sort by DisplayRank so index 0 is the primary course — keeps "first course active" and the
        // StudentCourseSwitcher's index===0 highlight well-defined regardless of wire order.
        this.EnrolledCourses = [...response.data].sort(
          (firstCourse, secondCourse) => firstCourse.DisplayRank - secondCourse.DisplayRank,
        );
        this.setActiveCourse(0);
      } else {
        showCustomMessage({
          messageText: `Failed to fetch enrolled courses. Please try again later.`,
          messageType: "danger",
          isSticky: true,
        });
      }
      this.DataLoading = false;
    },
    setActiveCourse(index: number) {
      if (index < 0 || index >= this.EnrolledCourses.length) {
        return;
      }
      this.ActiveCourseIndex = index;
      this.ActiveClassIndex = 0;
      this.SelectedSubject = null;
      this.SelectedSubjectChapterIds = [];
      this.resetStudentStoreData();
    },
    async getStudentSubjects() {
      await this.getEnrolledCourses();
      if (this.AllSubjects === null) {
        const appSettingsStore = useAppSettingsStore();
        const { AppName } = storeToRefs(appSettingsStore);
        showCustomMessage({
          messageText: `No subjects found - please contact ${AppName.value} customer support for assistance.`,
          messageType: "danger",
        });
        return null;
      }
      return this.AllSubjects;
    },
    async getStudentCourseSubjects() {
      await this.getEnrolledCourses();
      if (this.AllCourseSubjects === null) {
        const appSettingsStore = useAppSettingsStore();
        const { AppName } = storeToRefs(appSettingsStore);
        showCustomMessage({
          messageText: `No subjects found - please contact ${AppName.value} customer support for assistance.`,
          messageType: "danger",
        });
        return null;
      }
      return this.AllCourseSubjects;
    },
    async getClassesForStudent() {
      await this.getEnrolledCourses();
      if (this.AllClasses === null) {
        const appSettingsStore = useAppSettingsStore();
        const { AppName } = storeToRefs(appSettingsStore);
        showCustomMessage({
          messageText: `No class found - please contact ${AppName.value} customer support for assistance.`,
          messageType: "danger",
        });
        return null;
      }
      return this.AllClasses;
    },
    async getStudentSubjectCards() {
      const sessionStore = useSessionStore();
      const { UserInfo } = storeToRefs(sessionStore);
      const apiServicesStore = useApiServicesStore();
      const { userMetricsApi } = storeToRefs(apiServicesStore);
      if (!(this.ActiveCourse && this.ActiveClass)) {
        return null;
      }
      if (this.AllSubjectsCards[this.ActiveClass.Id] === undefined) {
        const response = await userMetricsApi.value.getStudentSubjectCards(
          UserInfo.value.UserId,
          this.ActiveCourse.Course.Id,
          this.ActiveClass.Id,
        );
        this.AllSubjectsCards[this.ActiveClass.Id] = response.isSuccess ? response.data : [];
        return this.AllSubjectsCards[this.ActiveClass.Id];
      }
      return this.AllSubjectsCards[this.ActiveClass.Id];
    },
    async getStudentRewardCoins() {
      if (this.AllRewardCoins !== null) {
        return;
      }
      const sessionStore = useSessionStore();
      const { UserInfo } = storeToRefs(sessionStore);
      const apiServicesStore = useApiServicesStore();
      const { userMetricsApi } = storeToRefs(apiServicesStore);
      const response = await userMetricsApi.value.getRewardCoinsForStudent(UserInfo.value.UserId);
      this.AllRewardCoins = response.isSuccess ? response.data : null;
    },
    async getStudentChapterwiseCards(subjectId: number) {
      if (!(this.ActiveCourse && this.ActiveClass)) {
        return;
      }
      const sessionStore = useSessionStore();
      const { UserInfo } = storeToRefs(sessionStore);
      const apiServicesStore = useApiServicesStore();
      const { userMetricsApi } = storeToRefs(apiServicesStore);
      const activeKey = `${this.ActiveClass.Id}_${subjectId}`;
      if (this.AllStudentContent[activeKey] === undefined) {
        const response = await userMetricsApi.value.getStudentChapterwiseCards(
          UserInfo.value.UserId,
          this.ActiveCourse.Course.Id,
          this.ActiveClass.Id,
          subjectId,
          true,
          UserInfo.value.IsDemoUser,
        );
        this.AllStudentContent[activeKey] = response.isSuccess ? response.data : {};
        return this.AllStudentContent[activeKey];
      }
      return this.AllStudentContent[activeKey];
    },
    async getAllStudentSubjectTrends(subjectId: number) {
      if (!(this.ActiveCourse && this.ActiveClass)) {
        return;
      }
      const sessionStore = useSessionStore();
      const { UserInfo } = storeToRefs(sessionStore);
      const apiServicesStore = useApiServicesStore();
      const { userMetricsApi } = storeToRefs(apiServicesStore);
      const activeKey = `${this.ActiveClass.Id}_${subjectId}`;
      if (this.AllSubjectTrends[activeKey] === undefined) {
        const response = await userMetricsApi.value.getStudentSubjectTrends(
          UserInfo.value.UserId,
          this.ActiveCourse.Course.Id,
          this.ActiveClass.Id,
          subjectId,
        );
        this.AllSubjectTrends[activeKey] = response.isSuccess ? response.data : [];
        return this.AllSubjectTrends[activeKey];
      }
      return this.AllSubjectTrends[activeKey];
    },
    async getAllStudentSubjectOverviewCards(subjectId: number) {
      if (!(this.ActiveCourse && this.ActiveClass)) {
        return;
      }
      const sessionStore = useSessionStore();
      const { UserInfo } = storeToRefs(sessionStore);
      const apiServicesStore = useApiServicesStore();
      const { userMetricsApi } = storeToRefs(apiServicesStore);
      const activeKey = `${this.ActiveClass.Id}_${subjectId}`;
      if (this.AllSubjectTrends[activeKey] === undefined) {
        const response = await userMetricsApi.value.getStudentSubjectOverviewCards(
          UserInfo.value.UserId,
          this.ActiveCourse.Course.Id,
          this.ActiveClass.Id,
          subjectId,
          UserInfo.value.IsDemoUser,
        );
        this.AllOverviewCards[activeKey] = response.isSuccess ? response.data : [];
        return this.AllOverviewCards[activeKey];
      }
      return this.AllOverviewCards[activeKey];
    },
    resetStudentStoreData() {
      this.AllSubjectsCards = {};
      this.AllRewardCoins = null;
      this.AllStudentContent = {};
      this.AllSubjectTrends = {};
      this.AllOverviewCards = {};
    },
  },
  persist: true,
});
